import { getStockStatusChecks, type StockStatusT } from '../../../../../../product/index';
import type { DateSerializable } from '@vs-shop/schemas/DateSerializable';
import type { MaxOrderable } from '../../../../../../shared/utils/stockValidation';
import { getAvailableQuantityWithCartInMind } from '../../../../../../shared/utils/stockValidation';

import {
  ProductStockDate,
  ProductStockIcon,
  ProductStockLabel,
  ProductStockLevel,
  type ProductStockMarkLevel,
  ProductStockNote,
  ProductStockRow,
  ProductStockSource,
} from '@vs-shop/commerce-ui/productStock';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../../../utils/intl';

import { StockQuantity } from './StockQuantity';
import DispatchDate from '../../../../product/DispatchDate';

import type { WarehouseT } from '../../../../../../enum/warehouse';
import { intlWarehouse } from '../../../../../../enum/warehouse';

type CommonProps = {
  quantity: MaxOrderable;
  date: DateSerializable | null;
  stockStatus: StockStatusT;
};

type Props = CommonProps &
  (
    | {
        isEshop: true;
        warehouseType?: never;
      }
    | {
        isEshop: false;
        warehouseType: WarehouseT;
      }
  );

/**
 * What the row's mark answers for: the product, not the place. Sold out is the
 * struck-out calendar; held AND orderable is the tick; anything else is awaited.
 */
const markLevel = (
  isSoldOut: boolean,
  isHeld: boolean,
  isOrderable: boolean
): ProductStockMarkLevel => {
  if (isSoldOut) return ProductStockLevel.UNAVAILABLE;
  return isHeld && isOrderable ? ProductStockLevel.AVAILABLE : ProductStockLevel.PENDING;
};

/**
 * What the e-shop's own copy is worth: nothing orderable reads as gone, and a
 * pre-order or a supplier's shelf reads as awaited however much is promised.
 */
const eshopLabelLevel = (isOrderable: boolean, isAwaited: boolean): ProductStockLevel => {
  if (!isOrderable) return ProductStockLevel.UNAVAILABLE;
  return isAwaited ? ProductStockLevel.PENDING : ProductStockLevel.AVAILABLE;
};

/**
 * One rcprofi availability row.
 *
 * Everything decided here is this tenant's: how much of the product a shopper
 * can still order once the cart is accounted for, which of the four stock
 * statuses that adds up to, and what the row says in Czech. The shared parts
 * take the level and the copy and paint the row.
 *
 * The mark and the label read the level separately because the two disagree in
 * one case the catalogue produces: an e-shop row for a product held at the
 * supplier carries the dated mark AND the warning colour, while a shop-floor
 * row that is merely empty carries the same colour under a mark answering for
 * the product as a whole.
 */
export default function ProductStockItem({
  isEshop,
  warehouseType,
  quantity,
  date,
  stockStatus,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const availableQuantity = getAvailableQuantityWithCartInMind(quantity, 0);
  const isInStock = availableQuantity > 0;

  const {
    isInStock: isInStockStatus,
    isPreOrder,
    isSupplierStock,
    isSoldOut,
  } = getStockStatusChecks(stockStatus);

  const iconLevel = markLevel(isSoldOut, isInStockStatus, isInStock);

  // A shop floor states only whether it is holding any — never "gone", because
  // the product's own status is already answered by the mark beside it.
  const labelLevel: ProductStockLevel = isEshop
    ? eshopLabelLevel(isInStock, isPreOrder || isSupplierStock)
    : isInStockStatus && isInStock
      ? ProductStockLevel.AVAILABLE
      : ProductStockLevel.PENDING;

  const source =
    isEshop && isSupplierStock
      ? intl.formatMessage({
          id: 'common.eshop.atTheSuppliers',
          defaultMessage: 'U dodavatele',
        })
      : isEshop
        ? intl.formatMessage({ id: 'common.eshop', defaultMessage: 'E-shop' })
        : intlWarehouse(warehouseType);

  const dispatchAction = isEshop
    ? intl.formatMessage({
        id: 'component.server.page.product.panel.productStockItem.action.dispatch',
        defaultMessage: 'odešleme',
      })
    : intl.formatMessage({
        id: 'component.server.page.product.panel.productStockItem.action.pickup',
        defaultMessage: 'k vyzvednutí',
      });

  return (
    <ProductStockRow>
      <ProductStockIcon variant={iconLevel} />

      <ProductStockLabel variant={labelLevel}>
        <ProductStockSource>{source}</ProductStockSource>
        <StockQuantity availableQuantity={availableQuantity} />
      </ProductStockLabel>

      {date !== null ? (
        <>
          <ProductStockNote className="mx-1">–</ProductStockNote>
          <ProductStockNote>{dispatchAction}</ProductStockNote>
          <ProductStockDate>
            <DispatchDate date={date} />
          </ProductStockDate>
        </>
      ) : null}
    </ProductStockRow>
  );
}
